/**
 * RtRecorderBridge — integration seam between the room UI and the local BWF
 * recording engine (packages/audio BwfSessionRecorder, built by S2).
 *
 * S4: real implementation. The bridge owns the actor-side capture chain
 *
 *   getUserMedia (pro settings: no EC/NS/AGC, mono)
 *     → AudioContext({sampleRate:48000}) MediaStreamAudioSourceNode
 *     → BwfSessionRecorder (48k/24-bit mono BWF, streamed to disk)
 *
 * plus the post-stop upload pipeline (frozen contract):
 *
 *   POST /rt/join/:token/takes            → take_id + R2 multipart upload_id
 *   POST /rt/takes/:id/parts/:n/presign   → presigned PUT per 10 MB part
 *   PUT <presigned url>                   → ETag
 *   POST /rt/takes/:id/complete           → uploaded (sidecar attached)
 *
 * v1 upload policy: serial parts, one retry per step, abort on final failure.
 */
import { BwfSessionRecorder, buildSidecar } from '@ohear/audio'
import type { RtSidecarMtcEntry, RtSidecarNetworkSample } from '@ohear/audio'
import recorderWorkletUrl from '@ohear/audio/src/worklets/recorder.worklet.ts?worker&url'
import { abortTake, completeTake, presignTakePart, registerTake } from '../lib/api'
import { samplesToTc } from '../lib/timecode'

export type RecorderState = 'idle' | 'armed' | 'recording'

export type StorageMode = 'unset' | 'user-dir' | 'opfs'

export interface RecorderArmOptions {
  take: number
  tcSamples: number
  /** e.g. ProjectX_Actor_T03_TC01003000 */
  fileBase: string
}

export interface RecorderStopResult {
  fileName: string
  bytes: number
  durationSec: number
}

export type TakeUploadStatus =
  | 'saving' // recorder still finalizing the local file
  | 'registering' // POST /rt/join/:token/takes in flight
  | 'uploading' // parts going up
  | 'uploaded' // complete confirmed by the API
  | 'failed' // gave up (after one retry per step)
  | 'local-only' // mock session / no API — file stays on disk

export interface TakeUpload {
  take: number
  fileName: string
  bytes: number
  durationSec: number
  takeId: string | null
  status: TakeUploadStatus
  /** 0..100; parts advance to 95, complete sets 100. */
  progressPct: number
  error?: string
}

export interface RecorderBridgeConfig {
  /** rt_session_links token — auth for the take upload endpoints. */
  linkToken: string
  sessionId: string
  projectCode: string | null
  actorName: string
  frameRateFps: number
  /** Offline demo: record locally, skip registration/upload. */
  mock: boolean
}

export interface RtRecorderBridge {
  readonly state: RecorderState
  readonly bytesWritten: number
  readonly storageMode: StorageMode
  readonly takes: TakeUpload[]
  /** Worklet 128-frame gap count for the CURRENT take (analyzer row 10). */
  readonly droppedFrames: number
  /** Latest measured disk-commit latency in ms (analyzer row 14); null until
   *  the first 3 s probe lands. Probed only while recording. */
  readonly lastCommitMs: number | null
  arm(opts: RecorderArmOptions): Promise<void>
  start(opts: { take: number; tcSamples: number; fileBase?: string }): Promise<void>
  stop(): Promise<RecorderStopResult | null>
  /**
   * Must be called inside a user gesture: shows the File System Access
   * directory picker; denied/unavailable → OPFS fallback.
   */
  chooseSaveFolder(): Promise<StorageMode>
  /** Feed studio 'tc' broadcasts (kept in a 200-entry ring buffer → sidecar). */
  noteTc(samples: number, wallMs: number): void
  /** Feed heartbeat network measurements (→ sidecar networkSamples). */
  noteNetworkSample(sample: RtSidecarNetworkSample): void
  dispose(): void
  onLevel?: (peakDbfs: number) => void
  onBytes?: (written: number) => void
  onTakesChanged?: (takes: TakeUpload[]) => void
  onStorageModeChanged?: (mode: StorageMode) => void
  onStateChanged?: (state: RecorderState) => void
}

const PRO_AUDIO_CAPTURE: MediaTrackConstraints = {
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl: false,
  channelCount: 1,
  sampleRate: 48000,
}

const MTC_RING_MAX = 200
const NET_RING_MAX = 200
const PART_BYTES = 10 * 1024 * 1024 // 10 MB multipart chunks (S3 min part 5 MB)

declare global {
  interface Window {
    showDirectoryPicker?: (options?: { mode?: 'read' | 'readwrite' }) => Promise<FileSystemDirectoryHandle>
  }
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

/** One retry on failure (v1 串行+失败重试1次). */
async function retryOnce<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn()
  } catch {
    return await fn()
  }
}

class BwfRecorderBridge implements RtRecorderBridge {
  state: RecorderState = 'idle'
  bytesWritten = 0
  storageMode: StorageMode = 'unset'
  takes: TakeUpload[] = []
  lastCommitMs: number | null = null

  get droppedFrames(): number {
    return this.recorder?.dropouts.length ?? 0
  }

  onLevel?: (peakDbfs: number) => void
  onBytes?: (written: number) => void
  onTakesChanged?: (takes: TakeUpload[]) => void
  onStorageModeChanged?: (mode: StorageMode) => void
  onStateChanged?: (state: RecorderState) => void

  private readonly cfg: RecorderBridgeConfig
  private context: AudioContext | null = null
  private stream: MediaStream | null = null
  private recorder: BwfSessionRecorder | null = null
  private deviceLabel: string | null = null

  private readonly mtcRing: RtSidecarMtcEntry[] = []
  private readonly netRing: RtSidecarNetworkSample[] = []

  private pendingFileBase: string | null = null
  private currentTake = 0
  private currentTcSamples = 0
  private disposed = false
  private commitProbeTimer: ReturnType<typeof setInterval> | null = null

  constructor(cfg: RecorderBridgeConfig) {
    this.cfg = cfg
  }

  // ------------------------------------------------------------ public API

  async chooseSaveFolder(): Promise<StorageMode> {
    const recorder = await this.ensureAudio()
    if (this.state !== 'idle') return this.storageMode // can't swap sink mid-take
    let handle: FileSystemDirectoryHandle | undefined
    if (typeof window.showDirectoryPicker === 'function') {
      try {
        handle = await window.showDirectoryPicker({ mode: 'readwrite' })
      } catch {
        handle = undefined // dismissed or denied → OPFS fallback
      }
    }
    const mode = await recorder.useDirectory(handle)
    this.setStorageMode(mode)
    return mode
  }

  async arm(opts: RecorderArmOptions): Promise<void> {
    if (this.state === 'recording') return
    this.pendingFileBase = opts.fileBase
    this.currentTake = opts.take
    this.currentTcSamples = opts.tcSamples
    try {
      const recorder = await this.ensureAudio()
      await this.ensureSink(recorder)
    } catch (err) {
      console.error('[rt-recorder] arm: audio init failed', err)
    }
    this.setState('armed')
  }

  async start(opts: { take: number; tcSamples: number; fileBase?: string }): Promise<void> {
    if (this.state === 'recording') return
    const recorder = await this.ensureAudio()
    await this.ensureSink(recorder)

    const fileBase =
      opts.fileBase ?? this.pendingFileBase ?? `${this.cfg.projectCode ?? 'RT'}_Take_T${pad2(opts.take)}`
    this.currentTake = opts.take
    this.currentTcSamples = opts.tcSamples
    this.bytesWritten = 0

    // Fresh telemetry for this take: recorder.stop() builds the on-disk .json
    // sidecar from these arrays, so sync the ring buffers in.
    recorder.mtcLog.length = 0
    recorder.networkSamples.length = 0
    recorder.dropouts.length = 0
    recorder.deviceLabel = this.deviceLabel

    await recorder.start({
      fileBase,
      tcStartSamples: opts.tcSamples,
      originator: this.cfg.actorName.slice(0, 32),
      originatorRef: this.cfg.sessionId.slice(0, 8),
      description: `take ${opts.take}`,
    })
    this.setState('recording')
    this.startCommitProbe()
  }

  /**
   * F3 v2.4 row 14 — disk-commit latency: every 3 s while recording, time a
   * tiny OPFS write-and-close (createWritable commits atomically on close).
   * Pure probe — touches a scratch file, never the recording engine or the
   * take's sink.
   */
  private startCommitProbe(): void {
    this.stopCommitProbe()
    if (!navigator.storage?.getDirectory) return
    const probe = async () => {
      try {
        const t0 = performance.now()
        const root = await navigator.storage.getDirectory()
        const fh = await root.getFileHandle('.rt-commit-probe', { create: true })
        const w = await fh.createWritable()
        await w.write(new Uint8Array(4096))
        await w.close()
        this.lastCommitMs = Math.round(performance.now() - t0)
      } catch {
        /* probe unavailable — leave the last value */
      }
    }
    this.commitProbeTimer = setInterval(() => void probe(), 3000)
    void probe()
  }

  private stopCommitProbe(): void {
    if (this.commitProbeTimer != null) {
      clearInterval(this.commitProbeTimer)
      this.commitProbeTimer = null
    }
  }

  async stop(): Promise<RecorderStopResult | null> {
    this.stopCommitProbe()
    if (this.state !== 'recording' || !this.recorder) {
      this.setState('idle')
      return null
    }
    const recorder = this.recorder

    // Snapshot telemetry into the recorder so the on-disk sidecar carries it.
    recorder.mtcLog.splice(0, recorder.mtcLog.length, ...this.mtcRing)
    recorder.networkSamples.splice(0, recorder.networkSamples.length, ...this.netRing)
    recorder.deviceLabel = this.deviceLabel

    const entry: TakeUpload = {
      take: this.currentTake,
      fileName: '',
      bytes: 0,
      durationSec: 0,
      takeId: null,
      status: 'saving',
      progressPct: 0,
    }
    this.takes = [...this.takes, entry]
    this.emitTakes()

    let result
    try {
      result = await recorder.stop()
    } catch (err) {
      this.setState('idle')
      entry.status = 'failed'
      entry.error = err instanceof Error ? err.message : 'recorder stop failed'
      this.emitTakes()
      return null
    }
    this.setState('idle')

    entry.fileName = result.fileName
    entry.bytes = result.bytes
    entry.durationSec = result.durationSec
    this.emitTakes()

    // Background upload — fire and forget; UI follows via onTakesChanged.
    void this.uploadTake(entry, result.getBlob)

    return { fileName: result.fileName, bytes: result.bytes, durationSec: result.durationSec }
  }

  noteTc(samples: number, wallMs: number): void {
    this.mtcRing.push({ samples, wallMs })
    if (this.mtcRing.length > MTC_RING_MAX) this.mtcRing.shift()
    // Live-feed the recorder too so a crash mid-take still has a partial log.
    if (this.state === 'recording' && this.recorder) {
      this.recorder.mtcLog.push({ samples, wallMs })
      if (this.recorder.mtcLog.length > MTC_RING_MAX) this.recorder.mtcLog.shift()
    }
  }

  noteNetworkSample(sample: RtSidecarNetworkSample): void {
    this.netRing.push(sample)
    if (this.netRing.length > NET_RING_MAX) this.netRing.shift()
  }

  dispose(): void {
    this.disposed = true
    this.stopCommitProbe()
    if (this.state === 'recording' && this.recorder) {
      void this.recorder.stop().catch(() => undefined)
    }
    this.stream?.getTracks().forEach((t) => t.stop())
    this.stream = null
    void this.context?.close().catch(() => undefined)
    this.context = null
    this.recorder = null
    this.state = 'idle'
  }

  // ------------------------------------------------------------- internals

  private async ensureAudio(): Promise<BwfSessionRecorder> {
    if (this.recorder) return this.recorder
    if (this.disposed) throw new Error('recorder bridge disposed')

    const context = new AudioContext({ sampleRate: 48000 })
    if (context.state === 'suspended') {
      void context.resume().catch(() => undefined)
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: PRO_AUDIO_CAPTURE })
    const track = stream.getAudioTracks()[0]
    this.deviceLabel = track?.label ?? null

    const source = context.createMediaStreamSource(stream)
    const recorder = new BwfSessionRecorder({ context, source, workletUrl: recorderWorkletUrl })
    recorder.onLevel = (db) => this.onLevel?.(db)
    recorder.onBytes = (written) => {
      this.bytesWritten = written
      this.onBytes?.(written)
    }

    this.context = context
    this.stream = stream
    this.recorder = recorder
    return recorder
  }

  private async ensureSink(recorder: BwfSessionRecorder): Promise<void> {
    if (this.storageMode !== 'unset') return
    const mode = await recorder.useDirectory() // no handle → OPFS
    this.setStorageMode(mode)
  }

  private async uploadTake(entry: TakeUpload, getBlob: () => Promise<Blob>): Promise<void> {
    const sidecar = {
      ...buildSidecar({
        fileBase: entry.fileName.replace(/\.wav$/, ''),
        tcStartSamples: this.currentTcSamples,
        sampleRateActual: this.context?.sampleRate ?? 48000,
        durationSec: entry.durationSec,
        dataBytes: Math.round(entry.durationSec * 48000) * 3,
        deviceLabel: this.deviceLabel,
        mtcLog: [...this.mtcRing],
        dropouts: this.recorder ? [...this.recorder.dropouts] : [],
        networkSamples: [...this.netRing],
      }),
      // v2.7 #3 — human-readable take-start TC next to tcStartSamples (the
      // bext TimeReference itself is written by the engine from tcStartSamples)
      tcStart: samplesToTc(this.currentTcSamples, this.cfg.frameRateFps),
    }

    if (this.cfg.mock) {
      entry.status = 'local-only'
      entry.progressPct = 100
      this.emitTakes()
      return
    }

    let takeId: string | null = null
    let uploadId: string | null = null
    try {
      entry.status = 'registering'
      this.emitTakes()

      const reg = await retryOnce(() =>
        registerTake(this.cfg.linkToken, {
          take_number: entry.take,
          filename: entry.fileName,
          tc_start_samples: this.currentTcSamples,
          sample_rate: 48000,
          bit_depth: 24,
          channels: 1,
          sidecar: sidecar as unknown as Record<string, unknown>,
        }),
      )
      takeId = reg.take_id
      uploadId = reg.upload_id
      entry.takeId = reg.take_id
      entry.status = 'uploading'
      this.emitTakes()

      const blob = await getBlob()
      const total = blob.size
      const parts: Array<{ n: number; etag: string }> = []
      for (let n = 1, offset = 0; offset < total; n += 1, offset += PART_BYTES) {
        const slice = blob.slice(offset, Math.min(offset + PART_BYTES, total))
        const { url } = await retryOnce(() => presignTakePart(this.cfg.linkToken, reg.take_id, n, reg.upload_id))
        const res = await retryOnce(async () => {
          const r = await fetch(url, { method: 'PUT', body: slice })
          if (!r.ok) throw new Error(`part ${n} PUT failed (${r.status})`)
          return r
        })
        const etag = res.headers.get('etag')
        if (!etag) throw new Error(`part ${n}: ETag not exposed (check bucket CORS)`)
        parts.push({ n, etag })
        entry.progressPct = Math.round((Math.min(offset + PART_BYTES, total) / total) * 95)
        this.emitTakes()
      }

      await retryOnce(() =>
        completeTake(this.cfg.linkToken, reg.take_id, {
          upload_id: reg.upload_id,
          parts,
          duration_sec: entry.durationSec,
          size_bytes: total,
          sidecar: sidecar as unknown as Record<string, unknown>,
        }),
      )
      entry.status = 'uploaded'
      entry.progressPct = 100
      this.emitTakes()
    } catch (err) {
      entry.status = 'failed'
      entry.error = err instanceof Error ? err.message : 'upload failed'
      this.emitTakes()
      if (takeId && uploadId) {
        void abortTake(this.cfg.linkToken, takeId, uploadId).catch(() => undefined)
      }
    }
  }

  private setState(state: RecorderState): void {
    this.state = state
    this.onStateChanged?.(state)
  }

  private setStorageMode(mode: StorageMode): void {
    this.storageMode = mode
    this.onStorageModeChanged?.(mode)
  }

  private emitTakes(): void {
    this.takes = [...this.takes]
    this.onTakesChanged?.(this.takes)
  }
}

/** Real BwfSessionRecorder-backed bridge (actor role only). */
export function createRecorderBridge(cfg: RecorderBridgeConfig): RtRecorderBridge {
  return new BwfRecorderBridge(cfg)
}
